/**
 * Typed errors surfaced by the HTTP client.
 *
 * The legacy code read axios error shape directly; here we wrap into a single
 * typed class so callers (and TanStack Query) can match on kind without
 * reaching for axios internals.
 */

import type { ZodError } from 'zod';

export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  readonly method: string;
  readonly body: unknown;

  constructor(params: {
    message: string;
    status: number;
    url: string;
    method: string;
    body?: unknown;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.status = params.status;
    this.url = params.url;
    this.method = params.method;
    this.body = params.body;
  }
}

/**
 * Raised when a server response fails Zod validation.
 *
 * This is how the Code Portability Contract's "validate responses so a backend
 * shape change fails fast" requirement manifests at runtime: instead of
 * silently passing malformed data through the app, callers see an explicit
 * error pointing at the URL that drifted.
 */
export class ResponseValidationError extends Error {
  readonly url: string;
  readonly method: string;
  readonly zodError: ZodError;
  readonly received: unknown;

  constructor(params: {
    url: string;
    method: string;
    zodError: ZodError;
    received: unknown;
  }) {
    super(
      `Response validation failed for ${params.method} ${params.url}: ${params.zodError.message}`
    );
    this.name = 'ResponseValidationError';
    this.url = params.url;
    this.method = params.method;
    this.zodError = params.zodError;
    this.received = params.received;
  }
}

/**
 * Raised when the caller asked the client to make a network request but no
 * fetch adapter was provided. The intended fix is to construct the client
 * with an explicit fetch adapter at the app boundary (T7).
 */
export class MissingFetchAdapterError extends Error {
  constructor() {
    super(
      'HttpClient has no fetch adapter. Inject one at construction: new HttpClient({ fetch: window.fetch.bind(window), ... }).'
    );
    this.name = 'MissingFetchAdapterError';
  }
}

/**
 * Extract a user-facing message from a thrown value caught at a UI boundary.
 *
 * Order of preference:
 *   1. ApiError.body.error — string field returned by all backend handlers
 *      for client errors (`{ error: "User is already a member" }`).
 *   2. Error.message — for non-API errors or ApiError without a body.
 *   3. fallback — for non-Error rejections.
 *
 * Use this in mutation `catch` blocks instead of `err.message`. The raw
 * `ApiError.message` is "<METHOD> <URL> failed: <status> <statusText>",
 * which leaks implementation details and hides the actionable reason.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | null | undefined;
    if (body && typeof body.error === 'string') {
      // Backend signalled an explicit reason. Use it if non-empty; treat an
      // empty string as "no reason given" and let the call site supply its
      // own fallback rather than leaking the raw HTTP-status message.
      return body.error.trim().length > 0 ? body.error : fallback;
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}
